"use client";

import { useEffect, useRef, useState } from "react";

import { Button, cn } from "@vllnt/ui";
import { Check, Copy } from "lucide-react";

type CodeBlockProps = {
  children: React.ReactNode;
  className?: string;
  language?: string;
  showLanguage?: boolean;
};

function stringifyChildren(children: React.ReactNode): string {
  if (children === null || children === undefined) return "";
  if (Array.isArray(children)) {
    return (children as React.ReactNode[]).map(stringifyChildren).join("");
  }
  if (typeof children === "object" && "props" in children) {
    const props = (children as { props?: { children?: React.ReactNode } })
      .props;
    if (props && "children" in props) {
      return stringifyChildren(props.children);
    }
  }
  return String(children);
}

function findScrollableParent(
  element: HTMLElement | null,
): HTMLElement | undefined {
  if (!element) return undefined;
  if (element.scrollHeight > element.clientHeight) return element;
  return findScrollableParent(element.parentElement);
}

export function CodeBlock({
  children,
  className,
  language,
  showLanguage = false,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const code = stringifyChildren(children);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const scrollable = findScrollableParent(element);
      if (scrollable) {
        scrollable.scrollTop += event.deltaY;
        event.preventDefault();
      }
    };
    // oxlint-disable-next-line react-doctor/client-passive-event-listeners
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      element.removeEventListener("wheel", onWheel);
    };
  }, []);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <div
      className={cn(
        "relative w-full overflow-hidden rounded-md border bg-background",
        className,
      )}
    >
      <div
        className="relative overflow-x-auto overflow-y-hidden touch-pan-y"
        ref={scrollRef}
      >
        <pre className="m-0 min-w-fit overflow-y-hidden bg-background p-4 text-sm">
          <code className="block bg-transparent font-mono text-sm">{code}</code>
        </pre>
        <div className="absolute right-2 top-2 flex items-center gap-2">
          {showLanguage && language ? (
            <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
              {language}
            </span>
          ) : null}
          <Button
            className="size-8"
            onClick={handleCopy}
            size="icon"
            variant="ghost"
          >
            {copied ? (
              <Check className="size-3" />
            ) : (
              <Copy className="size-3" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
